/**
 * Jediné místo v celém frontendu, které smí volat `setSchema` — všechny
 * strukturální mutace musí projít `execute(command)`, stejně jako
 * `canvas/store.tsx` (Epic 6) pro `CanvasMetadata`. Druhý nezávisle
 * debouncovaný autosave stream vedle canvasu — proto backend `updateSchema()`
 * běží přes `withProjectLock` (`projectStore.ts`), aby si obě mutace
 * navzájem nepřepsaly slice při souběžném zápisu.
 *
 * Epic 13: `execute()` navíc zaznamená command do sdíleného undo/redo
 * zásobníku (`command/stack.tsx`) — stejný vzor jako `canvas/store.tsx`,
 * viz jeho hlavička pro plné odůvodnění.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SchemaModel } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCommandStackInstance } from '../command/stack';
import type { SchemaCommand } from './commands';

export type SchemaSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SchemaStoreValue {
  schema: SchemaModel;
  execute: (command: SchemaCommand) => void;
  saveStatus: SchemaSaveStatus;
  saveNow: () => void;
}

const SchemaStoreContext = createContext<SchemaStoreValue | null>(null);

const AUTOSAVE_DEBOUNCE_MS = 800;

interface SchemaStoreProviderProps {
  projectId: string;
  initialSchema: SchemaModel;
  children: ReactNode;
}

export function SchemaStoreProvider({ projectId, initialSchema, children }: SchemaStoreProviderProps) {
  const [schema, setSchema] = useState(initialSchema);
  const [saveStatus, setSaveStatus] = useState<SchemaSaveStatus>('idle');
  const stack = useCommandStackInstance();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<SchemaModel | null>(null);
  const savingRef = useRef(false);

  const runPersist = useCallback(async () => {
    if (savingRef.current) return;
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    savingRef.current = true;
    setSaveStatus('saving');
    try {
      await api.updateSchema(projectId, next);
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      savingRef.current = false;
      if (pendingRef.current) void runPersist();
    }
  }, [projectId]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void runPersist();
  }, [runPersist]);

  const schedulePersist = useCallback(
    (next: SchemaModel) => {
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [flush]);

  const execute = useCallback(
    (command: SchemaCommand) => {
      setSchema((prev) => {
        const next = command.apply(prev);
        schedulePersist(next);
        return next;
      });
      stack.record('schema', command);
    },
    [schedulePersist, stack],
  );

  // Applier pro undo/redo (Epic 13) — viz `canvas/store.tsx` pro plné odůvodnění,
  // proč tohle NEvolá `stack.record`.
  const applyDirect = useCallback(
    (command: SchemaCommand, direction: 'forward' | 'backward') => {
      setSchema((prev) => {
        const next = direction === 'forward' ? command.apply(prev) : command.revert(prev);
        schedulePersist(next);
        return next;
      });
    },
    [schedulePersist],
  );

  useEffect(() => {
    stack.setApplier('schema', applyDirect);
    return () => stack.setApplier('schema', null);
  }, [stack, applyDirect]);

  const value = useMemo<SchemaStoreValue>(
    () => ({ schema, execute, saveStatus, saveNow: flush }),
    [schema, execute, saveStatus, flush],
  );

  return <SchemaStoreContext.Provider value={value}>{children}</SchemaStoreContext.Provider>;
}

export function useSchemaStore(): SchemaStoreValue {
  const context = useContext(SchemaStoreContext);
  if (!context) throw new Error('useSchemaStore musí být uvnitř SchemaStoreProvider.');
  return context;
}
